import React from 'react';
import round from 'lodash/round';
import GeneralChart from '@/components/city/GeneralChart';
import { CusEcharts } from '@/components/AsyncComponent';
import type { ECOption } from '@/components/CusEcharts';
import { useClintMediaQuery } from '@/utils/hooks';
import { CityContext } from '@/context/create-context';
import {
  WORK_EXPERIENCE_1_3,
  WORK_EXPERIENCE_CITY_MAP,
  WORK_EXPERIENCE_CITY_SEO_MAP,
} from '@/constants/job-title';
import { TAB_WORK_DISTRIBUTION } from '@/constants/city';
import { handleEchartsEvent } from '@/utils/common-utils';

const WorkDistribution = () => {
  const chartRef = React.useRef(null);
  const { name: cityName, experience_distributed: experienceDistributed } =
    React.useContext(CityContext);
  const isMobile = useClintMediaQuery();

  const config = React.useMemo<ECOption>(() => {
    const experienceKeys = Object.keys(experienceDistributed);
    const hasDefaultKey = experienceKeys.includes(WORK_EXPERIENCE_1_3);
    const totalCount = Object.values(experienceDistributed).reduce(
      (a, b) => a + b,
      0,
    );
    const data: any[] = Object.entries(WORK_EXPERIENCE_CITY_MAP)
      .map(([key, item]) => {
        const salaryRange = Reflect.get(experienceDistributed, key);
        if (!salaryRange) {
          return;
        }
        return {
          name: item,
          key: key,
          value: experienceDistributed[key],
          label: {
            show:
              key === WORK_EXPERIENCE_1_3 ||
              (!hasDefaultKey && key === experienceKeys[0]),
          },
        };
      })
      .filter(Boolean);

    const seoInfo = `${cityName}招聘市场更倾向于有几年工作经验的求职者呢？招聘市场${data
      .map(
        ({ key, value }) =>
          `${WORK_EXPERIENCE_CITY_SEO_MAP[key]}占比${round(
            (value / totalCount) * 100,
          )}%`,
      )
      .join('；')}。本数据统计来源于各大平台发布的公开数据，仅供参考。`;

    return {
      legend: {
        type: 'scroll',
        orient: 'horizontal',
        selectorPosition: 'end',
        bottom: isMobile ? '6%' : 0,
        left: 'center',
        padding: 0,
        itemGap: 20,
        icon: 'circle',
        itemHeight: 8,
        itemWidth: 16,
        selectedMode: false,
        textStyle: {
          fontWeight: 500,
          fontSize: 14,
          lineHeight: 18,
          backgroundColor: 'transparent',
          textBorderWidth: 0,
        },
        formatter: (name) => {
          const value = data.find((item) => item.name === name).value;
          return `\n${name}\n${round((value / totalCount) * 100)}%`;
        },
      },
      color: ['#ff9f40', '#fc8452', '#5388d8', '#f4be37', '#0284c7', '#0d2535'],
      series: [
        {
          type: 'pie',
          avoidLabelOverlap: true,
          label: {
            position: isMobile ? 'inside' : 'outside',
            backgroundColor: isMobile ? '#fff' : 'transparent',
            borderRadius: isMobile ? 8 : 0,
            padding: isMobile ? [8, 8, 5, 8] : 0,
            shadowBlur: isMobile ? 5 : 0,
            shadowColor: isMobile ? 'rgba(0, 0, 0, 0.05)' : '',
            shadowOffsetX: 0,
            shadowOffsetY: isMobile ? 10 : 0,
            color: '#000',
            alignTo: 'none',
            bleedMargin: 5,
            fontSize: 14,
            lineHeight: 18,
            formatter: (params) => {
              const { name, value } = params.data as any;
              const rateValue = round((value / totalCount) * 100);
              if (isMobile) {
                return `{name|${name}} {value|${rateValue}%}`;
              }
              return (
                '{icon' +
                params.dataIndex +
                '|●}' +
                `{name|${name}}` +
                '\n' +
                `{value|${rateValue}%}` +
                ''
              );
            },
            rich: {
              salary: { fontWeight: 'bold', fontSize: 14, lineHeight: 22 },
              icon0: {
                color: '#ff9f40',
                fontSize: 16,
                borderRadius: 4,
                padding: [0, 6, 0, 0],
              },
              icon1: {
                color: '#fc8452',
                fontSize: 16,
                borderRadius: 4,
                padding: [0, 6, 0, 0],
              },
              icon2: {
                color: '#5388d8',
                fontSize: 16,
                borderRadius: 4,
                padding: [0, 6, 0, 0],
              },
              icon3: {
                color: '#f4be37',
                fontSize: 16,
                borderRadius: 4,
                padding: [0, 6, 0, 0],
              },
              icon4: {
                color: '#0284c7',
                fontSize: 16,
                borderRadius: 4,
                padding: [0, 6, 0, 0],
              },
              icon5: {
                color: '#0d2535',
                fontSize: 16,
                borderRadius: 4,
                padding: [0, 6, 0, 0],
              },
            },
          },
          bottom: 40,
          labelLine: {
            show: true,
            showAbove: true,
          },
          cursor: 'default',
          emphasis: { disabled: true },
          data,
        },
      ],
      seoInfo,
    };
  }, [experienceDistributed, isMobile]);

  return (
    <GeneralChart
      title={`${cityName}招聘工作年限分布`}
      seoInfo={config.seoInfo}
      dataEleGroup={TAB_WORK_DISTRIBUTION}
    >
      <div className="relative w-full h-[310px]">
        <CusEcharts
          chartRef={chartRef}
          config={config}
          handleEvent={handleEchartsEvent}
        />
      </div>
    </GeneralChart>
  );
};

export default WorkDistribution;
